import { precise_round } from '@gameday/common/functions'
import { paymentDict } from '@gameday/common/functions/paymentDict'
import { getTeamNameByCode } from '@gameday/common/functions/team'
import { get as loGet } from 'lodash'
import { put, select, takeLatest } from 'redux-saga/effects'

import {
  getBraintreeId,
  getFormId,
  getFormInfo,
  getFormType,
  getInitialPaymentGateway,
  getIsGuestSignup,
  getIsTeamEvent,
  getOrderId,
  getPaymentGateways,
  getRegistrationData,
  getSelectedPaymentGateway,
  getSessionId,
  getTeamCode,
  getTeamFormType,
  getTeamsByCode,
  getUserId,
  getVoucherData,
  getVoucherSessionId,
} from 'selectors'

import { common, payment as paymentActions, voucher as voucherActions } from '../constants/actions'
import { formData, payment } from '../constants/endpoints'
import { convertFormType } from '../utils/common'
import { get, post } from '../utils/request'

export function* getBraintreePaymentToken(data) {
  const braintreeId = yield select(getBraintreeId)
  const { currency, vault } = data.payload
  const response = yield get({
    endpointId: payment.braintreeClientToken,
    params: {
      customerId: vault ? braintreeId : undefined,
      currency,
    },
  })

  if (response) {
    if (response.message) {
      yield put(paymentActions.setBraintreePaymentToken(response.message))
    } else {
      console.warn('Unexpected response structure: "message" is falsy.', response)
    }
  }
  yield put(common.setShowLoadingIndicator(false))
}

export function* submitPayment({ payload, callback }) {
  const {
    amount,
    surcharge,
    paymentMethodNonce,
    paymentType,
    firstName,
    lastName,
    email,
    phone,
    organisationName,
    organisation,
    descriptor,
    saveForLater,
    paymentToken,
  } = payload
  yield put(common.setShowLoadingIndicator(true))
  yield put(paymentActions.paymentSuccessful(true))
  const roundedAmount = Number(precise_round(amount, 2))
  const paymentGateways = yield select(getPaymentGateways)
  const merchantAccountId = loGet(
    (paymentGateways || []).find(pg => pg.name.toLowerCase() === 'braintree'),
    'merchantAccountId'
  )

  const formType = yield select(getFormType)
  const isTeamEvent = yield select(getIsTeamEvent)
  const teamFormType = yield select(getTeamFormType)
  const teamCode = yield select(getTeamCode)
  const teamsByCode = yield select(getTeamsByCode)
  const sessionId = yield select(getSessionId)
  const registrationData = yield select(getRegistrationData)
  const loggedInUser = yield select(getUserId)
  const initialGateway = yield select(getInitialPaymentGateway)
  const gateway = yield select(getSelectedPaymentGateway)
  const selectedGateway = gateway.parentId || gateway.id
  const updatedGateway = initialGateway !== selectedGateway
  const formInfo = yield select(getFormInfo)
  const type = convertFormType(formType) || convertFormType(formInfo?.form?.type) || ''
  const isTeamRego = isTeamEvent && teamFormType === 'Register A Team'
  const teamName = teamCode ? getTeamNameByCode(teamsByCode, teamCode) : null
  let source = 'Membership Form'

  switch (type.toLowerCase()) {
    case 'event':
      source = 'Event Form'
      break
    case 'shop':
      source = 'Shop Form'
      break
    default:
      source = 'Membership Form'
      break
  }

  const response = yield post({
    endpointId: payment.submitPayment,
    data: {
      amount: roundedAmount,
      paymentMethodNonce,
      paymentToken,
      orderId: yield select(getOrderId),
      firstName,
      lastName,
      email,
      phone,
      organisationName,
      organisation,
      descriptor,
      applicationId: 'synergy',
      source,
      merchantAccountId,
      braintreeId: yield select(getBraintreeId),
      userId: yield select(getUserId),
      saveForLater,
      skipVault: yield select(getIsGuestSignup),
      updateRegistration: {
        loggedInUser,
        formId: yield select(getFormId),
        orderId: yield select(getOrderId),
        paymentMethod: paymentDict(paymentType || gateway.name),
        amount: roundedAmount,
        session: sessionId || '',
        products: isTeamRego
          ? registrationData.teamProducts
          : Object.values(registrationData.members).reduce((a, r) => a.concat(r.products), []),
        ...(updatedGateway ? { paymentGateway: selectedGateway, updateOrderItem: updatedGateway } : {}),
        source,
        offline: false,
        teamName,
        teamDetails: registrationData.teamDetails,
        teamCode,
      },
    },
  })

  const { status, updatedRegistration } = response || {}

  if (status === 'success') {
    yield put(common.setStartTimer(false))

    if (updatedRegistration) {
      yield put(common.setShowLoadingIndicator(false))
      yield put(paymentActions.paymentSuccessful(true))
      yield put(paymentActions.redeemAndResetVouchers({}, callback))
    } else {
      yield put(
        paymentActions.submitPostPayment(
          {
            paymentType,
            amount: roundedAmount,
            surcharge,
            gatewayReference: response.id,
          },
          callback
        )
      )
    }
  } else {
    yield put(common.setShowLoadingIndicator(false))
    yield put(paymentActions.paymentSuccessful(false))
  }
  yield put(paymentActions.setBraintreePaymentToken(null))
}

export function* submitPostPayment({ payload, callback }) {
  const { amount, surcharge, gatewayReference, teamDetails, teamCode } = payload

  const registrationData = yield select(getRegistrationData)

  yield put(common.setShowLoadingIndicator(true))
  const formType = yield select(getFormType)
  const isTeamEvent = yield select(getIsTeamEvent)
  const teamFormType = yield select(getTeamFormType)
  const sessionId = yield select(getSessionId)
  const orderAmount = Number(precise_round(amount, 2))
  const gateway = yield select(getSelectedPaymentGateway)
  const initialGateway = yield select(getInitialPaymentGateway)
  const selectedGateway = gateway.parentId || gateway.id
  const updatedGateway = initialGateway !== selectedGateway
  const loggedInUser = yield select(getUserId)
  const orderId = yield select(getOrderId)
  const formInfo = yield select(getFormInfo)
  const type = convertFormType(formType) || convertFormType(formInfo?.form?.type) || ''

  const isTeamRego = isTeamEvent && teamFormType === 'Register A Team'
  const { teamName } = teamDetails || {}

  let source = 'Membership Form'

  switch (type.toLowerCase()) {
    case 'event':
      source = 'Event Form'
      break
    case 'shop':
      source = 'Shop Form'
      break
    default:
      source = 'Membership Form'
      break
  }

  const response = yield post({
    endpointId: formData.updateRegistrations,
    data: {
      loggedInUser,
      formId: yield select(getFormId),
      orderId,
      paymentMethod: paymentDict(payload.paymentType || gateway.name),
      gatewayReference,
      amount: orderAmount,
      surcharge,
      session: sessionId || '',
      products: isTeamRego
        ? registrationData.teamProducts
        : Object.values(registrationData.members).reduce((a, r) => a.concat(r.products), []),
      ...(isTeamRego && { ...registrationData }),
      source,
      offline: orderAmount === 0,
      ...(updatedGateway ? { paymentGateway: selectedGateway } : {}),
      ...(updatedGateway ? { updateOrderItem: updatedGateway } : {}),
      ...(teamDetails && {
        teamName,
        teamDetails,
      }),
      teamCode: teamCode || null,
    },
  })

  // biome-ignore lint/complexity/useOptionalChain: TODO
  if (response && response.success) {
    yield put(paymentActions.paymentSuccessful(true))
    yield put(paymentActions.redeemAndResetVouchers({}, callback))
  } else {
    yield put(paymentActions.paymentSuccessful(false))
    yield put(common.setShowLoadingIndicator(false))
  }
}

export function* redeemAndResetVouchers({ callback }) {
  const orderId = yield select(getOrderId)
  const formType = yield select(getFormType)
  const formInfo = yield select(getFormInfo)
  const voucherSessionId = yield select(getVoucherSessionId)
  const voucherData = yield select(getVoucherData)

  const type = convertFormType(formType) || convertFormType(formInfo?.form?.type) || ''
  let canRedeem = false
  if (Object.keys(voucherData).length) {
    Object.values(voucherData).forEach(vdata => {
      // biome-ignore lint/complexity/useOptionalChain: TODO
      if (vdata && vdata.appliedVoucher) {
        canRedeem = true
      }
    })
  }

  if (type.toLowerCase() === 'membership' || (type.toLowerCase() === 'shop' && canRedeem)) {
    yield put(voucherActions.redeemVouchers(orderId))
  }

  if (voucherSessionId) {
    yield put(voucherActions.resetVoucher(voucherSessionId))
  }

  yield put(common.setShowLoadingIndicator(false))

  callback()
}

export function* getOrderStatus(action) {
  const { payload } = action
  const { orderId } = payload

  if (!orderId) {
    return
  }

  yield put(common.setShowLoadingIndicator(true))

  const response = yield get({
    endpointId: payment.getOrderStatus,
    params: {
      orderId,
    },
  })

  // biome-ignore lint/complexity/useOptionalChain: TODO
  if (response && response.success && response.data) {
    yield put(paymentActions.setOrderStatus(response.data))
    yield put(common.setShowLoadingIndicator(false))

    return
  }

  yield put(common.setShowLoadingIndicator(false))
}

export function* runPaymentSagas() {
  yield takeLatest(paymentActions.GET_BRAINTREE_PAYMENT_TOKEN, getBraintreePaymentToken)
  yield takeLatest(paymentActions.SUBMIT_PAYMENT, submitPayment)
  yield takeLatest(paymentActions.SUBMIT_POST_PAYMENT, submitPostPayment)
  yield takeLatest(paymentActions.REDEEM_AND_RESET_VOUCHERS, redeemAndResetVouchers)
  yield takeLatest(paymentActions.GET_ORDER_STATUS, getOrderStatus)
}
